import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getGoogleWorkspaceAuthUrl,
  getGoogleWorkspacePickerSession,
  getGoogleWorkspaceSheetColumns,
  getGoogleWorkspaceSheets,
  getGoogleWorkspaceStatus,
} from '../../api';
import {
  loadGoogleApiScript,
  openGoogleAuthWindow,
  openSpreadsheetPicker,
  parseGoogleWorkspaceError,
  pickerSessionErrorMessage,
} from '../../crm/utils/googleWorkspaceUtils';
import { CustomSelect } from '@/components';
import {
  headerToPlaceholder,
  NATIVE_SHEET_MIME,
  SHEET_NO_MATCH_DEFAULT,
  sheetFoundNextOrder,
  sheetNotFoundNextOrder,
} from '../utils/googleSheetStep';

function SheetRouteSelect({
  label,
  tip,
  value,
  steps,
  currentOrder,
  blockedOrder,
  disabled,
  onChange,
}) {
  const options = [
    { value: '', label: 'No next node' },
    ...(steps || [])
      .filter((item) => Number(item.order) !== Number(currentOrder))
      .filter((item) => Number(item.order) !== Number(blockedOrder))
      .map((item) => ({
        value: String(item.order),
        label: `${item.order}. ${item.question_text || 'Step'}`,
      })),
  ];
  return (
    <div className="space-y-1.5 min-w-0">
      <label className="flex items-center text-sm font-medium text-text-secondary">
        {label}
        <Tip text={tip} />
      </label>
      <CustomSelect
        value={value == null ? '' : String(value)}
        disabled={disabled}
        onChange={(val) => onChange(val ? Number(val) : null)}
        options={options}
        hideCheckIcon
        textSizeClass="text-body-sm"
        buttonClassName={`${selectButtonClass} ${
          disabled ? 'opacity-60 cursor-not-allowed' : ''
        } border-transparent`}
      />
    </div>
  );
}

function Tip({ text }) {
  return (
    <span className="relative inline-flex align-middle group">
      <span
        className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-border-default text-[10px] font-medium text-text-muted"
        aria-label={text}
      >
        ?
      </span>
      <span className="pointer-events-none absolute left-0 top-6 z-30 hidden w-52 rounded-lg bg-[#1E2340] px-2.5 py-2 text-[11px] font-normal leading-snug text-white shadow-lg group-hover:block">
        {text}
      </span>
    </span>
  );
}

function previewReply(template, columnToken) {
  const sample = {
    name: 'John',
    1: 'John',
    brand: 'Acme',
    2: 'Acme',
  };
  if (columnToken) sample[columnToken] = 'Shipped';
  const text = String(template || '').replace(
    /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
    (_, key) => (sample[key] != null ? sample[key] : `{{${key}}}`),
  );
  return text.trim() || 'Your reply will show here.';
}

const GW_CALLBACK_URL = `${window.location.origin}/integrations/google-workspace/callback`;

const fieldClass =
  'w-full min-w-0 bg-[#F5F5F5] text-text-primary border border-transparent rounded-[6px] px-3 text-sm font-sf-pro-display outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15 transition-all disabled:opacity-60 disabled:cursor-not-allowed';
const areaClass = `${fieldClass} py-2.5 resize-none leading-relaxed`;
const selectButtonClass = `w-full min-w-0 h-10 flex items-center justify-between gap-2 rounded-[6px] bg-[#F5F5F5] border text-text-primary text-sm font-sf-pro-display focus:outline-none focus:border-primary px-3 transition-all`;

export function GoogleSheetStepFields({
  companyId,
  step,
  steps = [],
  canEdit,
  showErrors,
  onChange,
}) {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const replyRef = useRef(null);

  const {
    data: status,
    refetch,
    isLoading: statusLoading,
  } = useQuery({
    queryKey: ['google-workspace-status', companyId],
    queryFn: () => getGoogleWorkspaceStatus(companyId),
    enabled: !!companyId,
    staleTime: 30_000,
  });

  const connected = Boolean(status?.is_connected);

  const { data: sheetsData, isFetching: sheetsLoading } = useQuery({
    queryKey: ['google-workspace-sheets', companyId],
    queryFn: () => getGoogleWorkspaceSheets(companyId),
    enabled: !!companyId && connected,
    staleTime: 30_000,
  });

  useEffect(() => {
    const onAuthDone = (event) => {
      if (event.data?.type !== 'gw-auth-done') return;
      if (event.data.status === 'error') {
        toast.error(event.data.message || 'Google sign-in did not finish.');
        return;
      }
      if (event.data.status === 'success') refetch();
    };
    const onFocus = () => refetch();
    window.addEventListener('message', onAuthDone);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('message', onAuthDone);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetch]);

  const files = Array.isArray(sheetsData?.files) ? sheetsData.files : [];
  const selectedId = step.google_sheet_spreadsheet_id || '';
  const sheetName = String(step.google_sheet_name || '').trim();

  const {
    data: columnData,
    isFetching: columnsLoading,
    isError: columnsError,
  } = useQuery({
    queryKey: ['google-sheet-columns', companyId, selectedId, sheetName],
    queryFn: () =>
      getGoogleWorkspaceSheetColumns(companyId, {
        spreadsheet_id: selectedId,
        ...(sheetName ? { sheet_name: sheetName } : {}),
      }),
    enabled: !!companyId && connected && !!selectedId,
    staleTime: 30_000,
  });
  const selectedFile = files.find((file) => file.id === selectedId);
  const availableSheets = Array.isArray(columnData?.available_sheets)
    ? columnData.available_sheets
    : [];
  const columns = Array.isArray(columnData?.columns) ? columnData.columns : [];
  const sheetVariables = Array.isArray(columnData?.variables)
    ? columnData.variables
    : [];

  useEffect(() => {
    if (!selectedId || !columnData?.sheet_name) return;
    if (columnData.spreadsheet_id && columnData.spreadsheet_id !== selectedId) {
      return;
    }
    if (!sheetName) {
      onChange({ google_sheet_name: columnData.sheet_name });
    }
  }, [columnData, selectedId, sheetName, onChange]);

  const connect = async () => {
    if (!companyId || !canEdit) return;
    setConnecting(true);
    try {
      const res = await getGoogleWorkspaceAuthUrl(companyId, GW_CALLBACK_URL);
      const authUrl = res?.auth_url ?? res?.url;
      if (!authUrl) {
        toast.error('Could not retrieve Google authorization URL.');
        return;
      }
      sessionStorage.setItem('gw_company_id', String(companyId));
      localStorage.setItem('gw_company_id', String(companyId));
      const popup = openGoogleAuthWindow(authUrl);
      if (popup) {
        const timer = setInterval(() => {
          if (!popup.closed) return;
          clearInterval(timer);
          queryClient.invalidateQueries({
            queryKey: ['google-workspace-status', companyId],
          });
        }, 500);
      }
    } catch (err) {
      const parsed = parseGoogleWorkspaceError(err);
      toast.error(parsed.message || 'Failed to initiate Google sign-in.');
    } finally {
      setConnecting(false);
    }
  };

  const pickSheet = async () => {
    if (!companyId || !canEdit) return;
    const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
    if (!apiKey) {
      toast.error('VITE_GOOGLE_PICKER_API_KEY is missing.');
      return;
    }
    setPickerLoading(true);
    try {
      const session = await getGoogleWorkspacePickerSession(companyId);
      await loadGoogleApiScript();
      await openSpreadsheetPicker({
        accessToken: session.access_token,
        appId: session.app_id,
        apiKey,
        mimeTypes: NATIVE_SHEET_MIME,
        onPicked: (file) => {
          onChange({
            google_sheet_spreadsheet_id: file.id,
            google_sheet_file_name: file.name || '',
            google_sheet_name: '',
            google_sheet_lookup_column: '',
          });
        },
      });
    } catch (err) {
      const sessionMessage = pickerSessionErrorMessage(err);
      toast.error(
        sessionMessage ||
          parseGoogleWorkspaceError(err).message ||
          'Could not open Google Picker.',
      );
      if (sessionMessage) refetch();
    } finally {
      setPickerLoading(false);
    }
  };

  const sheetOptions = [
    { value: '', label: 'Select a Google Sheet' },
    ...files.map((file) => ({ value: file.id, label: file.name || file.id })),
  ];
  if (selectedId && !sheetOptions.some((opt) => opt.value === selectedId)) {
    sheetOptions.push({
      value: selectedId,
      label: step.google_sheet_file_name || 'Selected sheet',
    });
  }

  const missingSheet = showErrors && !selectedId;
  const missingColumn =
    showErrors && !String(step.google_sheet_lookup_column || '').trim();
  const missingTemplate =
    showErrors && !String(step.google_sheet_reply_template || '').trim();
  const columnToken = headerToPlaceholder(step.google_sheet_lookup_column);
  const replyText = step.google_sheet_reply_template || '';
  const noMatchText =
    step.google_sheet_no_match_message || SHEET_NO_MATCH_DEFAULT;

  const insertToken = (token) => {
    if (!canEdit || !token) return;
    const el = replyRef.current;
    const current = replyText;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    onChange({ google_sheet_reply_template: next });
    requestAnimationFrame(() => {
      if (!replyRef.current) return;
      const pos = start + token.length;
      replyRef.current.focus();
      replyRef.current.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="space-y-3 min-w-0 w-full text-left">
      {statusLoading ? (
        <div className="space-y-3 rounded-[8px] border border-border-default bg-[#F5F5F5] p-3 flex items-center justify-center min-h-[90px]">
          <p className="text-xs text-text-muted animate-pulse">
            Checking connection status...
          </p>
        </div>
      ) : !connected ? (
        <div className="space-y-3 rounded-[8px] border border-border-default bg-[#F5F5F5] p-3">
          <p className="text-xs text-text-muted leading-snug">
            Connect the company Google account. Only a native Google Sheet
            works. Uploaded Excel and CSV files are skipped.
          </p>
          <button
            type="button"
            disabled={!canEdit || connecting}
            onClick={connect}
            className="h-10 w-full sm:w-auto px-4 rounded-[8px] bg-primary text-white text-sm font-medium hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {connecting ? 'Opening Google...' : 'Connect Google'}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <section className="space-y-3 min-w-0 rounded-xl border border-border-default bg-white p-4">
            <h3 className="text-sm font-medium text-text-primary">
              1. Choose the sheet
            </h3>
            <div className="space-y-1.5 min-w-0">
              <label className="flex items-center text-sm font-medium text-text-secondary">
                Google Sheet
                <Tip text="Native Google Sheet only. Excel and CSV uploads are not used." />
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center min-w-0">
                <CustomSelect
                  className="flex-1 min-w-0"
                  value={selectedId}
                  disabled={!canEdit || sheetsLoading}
                  onChange={(val) => {
                    const file = files.find((item) => item.id === val);
                    onChange({
                      google_sheet_spreadsheet_id: val,
                      google_sheet_file_name: file?.name || '',
                      google_sheet_name: '',
                      google_sheet_lookup_column: '',
                    });
                  }}
                  options={sheetOptions}
                  hideCheckIcon
                  textSizeClass="text-body-sm"
                  buttonClassName={`${selectButtonClass} ${
                    !canEdit || sheetsLoading
                      ? 'opacity-60 cursor-not-allowed'
                      : ''
                  } ${missingSheet ? 'border-red-400' : 'border-transparent'}`}
                />
                <button
                  type="button"
                  disabled={!canEdit || pickerLoading}
                  onClick={pickSheet}
                  className="h-10 shrink-0 px-3 rounded-[6px] border border-primary text-primary text-sm font-medium bg-white hover:bg-surface-mint disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {pickerLoading ? 'Opening...' : 'Browse'}
                </button>
              </div>
              {selectedFile?.url && (
                <a
                  href={selectedFile.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-primary"
                >
                  Open sheet
                </a>
              )}
              {sheetsLoading && (
                <p className="text-xs text-text-muted">Loading sheets...</p>
              )}
              {missingSheet && (
                <p className="text-xs text-red-600">Select a Google Sheet.</p>
              )}
            </div>

            <div className="space-y-1.5 min-w-0">
              <label className="flex items-center text-sm font-medium text-text-secondary">
                Sheet tab
                <Tip text="Which tab to read. Changing the tab reloads its columns." />
              </label>
              <CustomSelect
                value={sheetName || columnData?.sheet_name || ''}
                disabled={!canEdit || !selectedId || columnsLoading}
                isLoading={columnsLoading}
                onChange={(val) =>
                  onChange({
                    google_sheet_name: val,
                    google_sheet_lookup_column: '',
                  })
                }
                options={(availableSheets.length
                  ? availableSheets
                  : sheetName
                    ? [sheetName]
                    : []
                ).map((name) => ({ value: name, label: name }))}
                hideCheckIcon
                textSizeClass="text-body-sm"
                buttonClassName={`${selectButtonClass} ${
                  !canEdit || !selectedId ? 'opacity-60 cursor-not-allowed' : ''
                } border-transparent`}
              />
            </div>

            <div className="space-y-1.5 min-w-0">
              <label className="flex items-center text-sm font-medium text-text-secondary">
                Column to match
                <Tip text="Header in row 1. Matching ignores case and extra spaces." />
              </label>
              <CustomSelect
                value={step.google_sheet_lookup_column || ''}
                disabled={!canEdit || !selectedId || columnsLoading}
                isLoading={columnsLoading}
                onChange={(val) =>
                  onChange({ google_sheet_lookup_column: val })
                }
                options={[
                  { value: '', label: 'Select a column...' },
                  ...Array.from(
                    new Set(
                      [
                        ...columns,
                        step.google_sheet_lookup_column || '',
                      ].filter(Boolean),
                    ),
                  ).map((col) => ({ value: col, label: col })),
                ]}
                hideCheckIcon
                textSizeClass="text-body-sm"
                buttonClassName={`${selectButtonClass} ${
                  !canEdit || !selectedId ? 'opacity-60 cursor-not-allowed' : ''
                } ${missingColumn ? 'border-red-400' : 'border-transparent'}`}
              />
              {!selectedId && (
                <p className="text-xs text-text-muted">Choose a sheet first.</p>
              )}
              {selectedId &&
                !columnsLoading &&
                columns.length === 0 &&
                !columnsError && (
                  <p className="text-xs text-text-muted">
                    No headers found. Please ensure row 1 of your sheet contains
                    column titles.
                  </p>
                )}
              {columnsError && (
                <p className="text-xs text-red-600">
                  Could not load columns for this sheet.
                </p>
              )}
              {missingColumn && (
                <p className="text-xs text-red-600">
                  Choose the column to match.
                </p>
              )}
            </div>
          </section>

          <section className="space-y-3 min-w-0 rounded-xl border border-border-default bg-white p-4">
            <h3 className="text-sm font-medium text-text-primary">
              2. WhatsApp reply
            </h3>
            <div className="space-y-1.5 min-w-0">
              <label className="flex items-center text-sm font-medium text-text-secondary">
                Reply when found
                <Tip text="Sent after a match, then the flow follows Next Node (If Found)." />
              </label>
              <div className="mb-2">
                <CustomSelect
                  value=""
                  onChange={(val) => {
                    if (val) insertToken(val);
                  }}
                  options={[
                    { value: '{{name}}', label: 'Customer name' },
                    { value: '{{brand}}', label: 'Company name' },
                    ...(selectedId
                      ? sheetVariables
                          .filter((item) => {
                            const token = String(item.placeholder || '');
                            return (
                              token &&
                              token !== '{{name}}' &&
                              token !== '{{1}}' &&
                              token !== '{{brand}}' &&
                              token !== '{{2}}'
                            );
                          })
                          .filter(
                            (item, index, list) =>
                              list.findIndex(
                                (other) =>
                                  other.placeholder === item.placeholder,
                              ) === index,
                          )
                          .map((item) => ({
                            value: item.placeholder,
                            label: item.column || item.variable,
                          }))
                      : []),
                  ]}
                  searchable
                  searchPlaceholder="Search variables..."
                  placeholder="+ Insert variable"
                  disabled={!canEdit}
                  hideCheckIcon
                  textSizeClass="text-xs font-medium"
                  buttonClassName={`h-8 rounded-full border border-border-default bg-[#F5F5F5] px-3 flex items-center justify-between gap-2 text-xs font-medium text-text-primary hover:border-primary disabled:opacity-60 focus:outline-none w-full sm:w-64`}
                />
              </div>
              {selectedId && columnsLoading && (
                <p className="text-xs text-text-muted">
                  Loading sheet columns...
                </p>
              )}
              <textarea
                ref={replyRef}
                rows={3}
                disabled={!canEdit}
                value={replyText}
                placeholder="Hello {{name}}, status is {{Status}}"
                onChange={(e) =>
                  onChange({ google_sheet_reply_template: e.target.value })
                }
                className={`${areaClass} ${
                  missingTemplate ? 'border-red-400' : ''
                }`}
              />
              {missingTemplate && (
                <p className="text-xs text-red-600">
                  Enter the reply template.
                </p>
              )}
            </div>
            <SheetRouteSelect
              label="If a match is found, go to"
              tip="Optional. After the found reply, continue at this step. Leave empty to keep the current next step."
              value={sheetFoundNextOrder(step)}
              steps={steps}
              currentOrder={step.order}
              blockedOrder={sheetNotFoundNextOrder(step)}
              disabled={!canEdit}
              onChange={(order) =>
                onChange({
                  next_step_order: order,
                  google_sheet_found_next_step_order: order,
                })
              }
            />

            <div className="rounded-[10px] bg-[#ECE5DD] p-3">
              <p className="mb-2 text-xs font-medium text-[#54656F]">
                What the customer will see
              </p>
              <div className="max-w-full rounded-lg rounded-tl-none bg-[#D9FDD3] px-3 py-2 text-sm text-[#111B21] shadow-sm">
                <p className="whitespace-pre-wrap break-words leading-snug">
                  {previewReply(replyText, columnToken)}
                </p>
                <p className="mt-1 text-right text-[10px] text-[#667781]">
                  10:30
                </p>
              </div>
            </div>

            <div className="space-y-1.5 min-w-0">
              <label className="flex items-center text-sm font-medium text-text-secondary">
                Reply when not found
                <Tip text="Sent when nothing matches. Then the flow follows Next Node (If Not Found), or stays here if that route is empty." />
              </label>
              <textarea
                rows={2}
                disabled={!canEdit}
                value={noMatchText}
                onChange={(e) =>
                  onChange({ google_sheet_no_match_message: e.target.value })
                }
                className={areaClass}
              />
            </div>
            <SheetRouteSelect
              label="If nothing matches, go to"
              tip="Optional. After the not-found reply, continue at this step. Leave empty to stay on this step."
              value={sheetNotFoundNextOrder(step)}
              steps={steps}
              currentOrder={step.order}
              blockedOrder={sheetFoundNextOrder(step)}
              disabled={!canEdit}
              onChange={(order) =>
                onChange({
                  google_sheet_no_match_next_step_order: order,
                  google_sheet_not_found_next_step_order: order,
                })
              }
            />
          </section>
        </div>
      )}
    </div>
  );
}
